import {
  Card,
  CircleLoader,
  ConfirmationModal,
  DangerButton,
  Header,
  IconButton,
  PrimaryButton,
  Space,
  WithTooltip,
} from '@procurify/ui'
import { useHistory } from 'react-router'

import { TabContentFooter } from '@webapp/components/TabContentFooter'
import { TabPanes } from '@webapp/components/TabPanes'
import { PageActions } from '@webapp/core/components/Page'
import { useFeatureFlag, useProcurifyIntl } from '@webapp/hooks'
import { ExpenseStatusLabel } from '@webapp/modules/expense/components/ExpenseStatusLabel'
import { ExpenseApprovalHistoryPresenter } from '@webapp/modules/expense/features/get-expense-approval-history/presenters/ExpenseApprovalHistoryPresenter'
import { ExpenseCostSummaryPresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpenseCostSummaryPresenter'
import { ExpensePresenter } from '@webapp/modules/expense/features/get-expense-report-detail/presenters/ExpensePresenter'
import { ApprovalReminderIconButton } from '@webapp/modules/expense/features/send-approval-reminder/presenters/ApprovalReminderIconButton'
import { SpendTrackerPresenter } from '@webapp/modules/expense/features/show-budget-info/presenters/SpendTrackerPresenter'
import {
  ApprovalFormSection,
  AuditLogTabContent,
  ExpenseCommentPopoverButton,
} from './components'
import messages from './messages'
import {
  ExpenseDetailViewStyled,
  MainContentStyled,
  SpendTrackerSidebarStyled,
} from './styles'
import { useExpenseView } from './useExpenseView'

export interface IExpenseViewProps {
  uuid: string
  onPermissionsLoaded?: (data: { canApprove: boolean }) => void
}

export const ExpenseView = ({
  uuid,
  onPermissionsLoaded,
}: IExpenseViewProps) => {
  const intl = useProcurifyIntl()
  const history = useHistory()
  const FT_5817_ENABLE_BUDGETS_REDESIGN = useFeatureFlag(
    'FT_5817_ENABLE_BUDGETS_REDESIGN'
  )

  const {
    data: { expense, isLoading },
    states: {
      showSpendTracker,
      isRequester,
      showSpendTrackerButton,
      showComments,
      expenseContentTypeId,
      userId,
      isApproving,
      isOverBudgetConfirmOpen,
      isDenying,
      isDeleting,
      isDeleteConfirmOpen,
      isDuplicating,
      isDuplicateConfirmOpen,
      activeTab,
      canApprove,
      canDelete,
      showEditButton,
      showReviseButton,
      isPending,
      hasNextApproverChoices,
      isFinalApproval,
      nextApprover,
      showSendReminder,
      showHistory,
      showGeneratePdf,
      nextApproverOptions,
      selectedNextApproverOption,
    },
    commands: {
      approveExpense,
      confirmOverBudget,
      cancelOverBudget,
      denyExpense,
      deleteExpenseReport,
      openDeleteConfirm,
      closeDeleteConfirm,
      duplicateExpense,
      confirmDuplicate,
      closeDuplicateConfirm,
      openPdf,
    },
    methods: {
      onSelectedNextApproverIdChange,
      onToggleSpendTracker,
      changeActiveTab,
    },
  } = useExpenseView({ uuid, onPermissionsLoaded })

  if (isLoading) {
    return <CircleLoader />
  }

  return (
    <>
      <PageActions>
        <Space direction='horizontal'>
          {isRequester && (
            <WithTooltip
              title={intl.formatMessage(messages.duplicateToolbarButton)}
            >
              <IconButton
                icon='duplicate'
                aria-label={intl.formatMessage(messages.duplicateToolbarButton)}
                onClick={duplicateExpense}
                disabled={isDuplicating}
              />
            </WithTooltip>
          )}

          {showHistory && expense && (
            <ExpenseApprovalHistoryPresenter logs={expense.logs} />
          )}

          {showComments && expense && (
            <ExpenseCommentPopoverButton
              contentTypeId={expenseContentTypeId}
              objectId={expense.id}
              userId={userId}
            />
          )}

          {showGeneratePdf && expense && (
            <WithTooltip
              title={intl.formatMessage(messages.generatePdfToolbarButton)}
            >
              <IconButton
                icon='print'
                aria-label={intl.formatMessage(
                  messages.generatePdfToolbarButton
                )}
                onClick={openPdf}
              />
            </WithTooltip>
          )}

          {showSendReminder && expense && nextApprover && (
            <ApprovalReminderIconButton
              expenseId={expense.id}
              recipient={nextApprover}
            />
          )}

          {showSpendTrackerButton && (
            <WithTooltip
              title={intl.formatMessage(
                FT_5817_ENABLE_BUDGETS_REDESIGN
                  ? messages.budgetsToolbarButton
                  : messages.spendTrackerToolbarButton
              )}
            >
              <IconButton
                icon='report'
                aria-label={intl.formatMessage(
                  FT_5817_ENABLE_BUDGETS_REDESIGN
                    ? messages.budgetsToolbarButton
                    : messages.spendTrackerToolbarButton
                )}
                onClick={onToggleSpendTracker}
              />
            </WithTooltip>
          )}
        </Space>
      </PageActions>

      <ExpenseDetailViewStyled showSpendTracker={showSpendTracker}>
        <MainContentStyled>
          <Space size='large'>
            <Card>
              <Card.Content>
                <Space>
                  {expense && (
                    <Space direction='horizontal' align='center'>
                      <Header as='h2'>
                        {intl.formatMessage(messages.reportHeaderTitle, {
                          id: expense.id,
                          title: expense.title,
                        })}
                      </Header>
                      <ExpenseStatusLabel
                        size='medium'
                        status={expense.status}
                      />
                    </Space>
                  )}

                  <TabPanes activeKey={activeTab} onChange={changeActiveTab}>
                    <TabPanes.Pane
                      label={intl.formatMessage(messages.detailsTabLabel)}
                      key='detail'
                    >
                      <ExpensePresenter
                        uuid={uuid}
                        showCostSummary={false}
                        showHeader={false}
                      />

                      <TabContentFooter isSticky>
                        <Space direction='vertical'>
                          <Space direction='horizontal' justify='end'>
                            <Space.Item>
                              <ExpenseCostSummaryPresenter uuid={uuid} />
                            </Space.Item>
                          </Space>
                          {canApprove && isPending && (
                            <ApprovalFormSection
                              nextApproverOptions={nextApproverOptions}
                              selectedNextApproverOption={
                                selectedNextApproverOption
                              }
                              onSelectedNextApproverIdChange={
                                onSelectedNextApproverIdChange
                              }
                              hasNextApproverChoices={hasNextApproverChoices}
                              isFinalApproval={isFinalApproval}
                              isApproving={isApproving}
                              isDenying={isDenying}
                              onApprove={approveExpense}
                              onDeny={denyExpense}
                            />
                          )}
                        </Space>
                      </TabContentFooter>
                    </TabPanes.Pane>

                    <TabPanes.Pane
                      label={intl.formatMessage(messages.auditLogTabLabel)}
                      key='audit-log'
                    >
                      {expense && (
                        <AuditLogTabContent
                          expenseContentTypeId={expenseContentTypeId}
                          expenseId={String(expense.id)}
                        />
                      )}
                    </TabPanes.Pane>
                  </TabPanes>
                </Space>
              </Card.Content>
            </Card>

            <Space direction='horizontal' justify='end'>
              {canDelete && (
                <DangerButton onClick={openDeleteConfirm} disabled={isDeleting}>
                  {intl.formatMessage(messages.deleteActionButton)}
                </DangerButton>
              )}
              {showEditButton && (
                <PrimaryButton
                  onClick={() => history.push(`/expenses/${uuid}/edit`)}
                >
                  {intl.formatMessage(messages.editActionButton)}
                </PrimaryButton>
              )}
              {showReviseButton && (
                <PrimaryButton
                  onClick={() => history.push(`/expenses/${uuid}/revise`)}
                >
                  {intl.formatMessage(messages.reviseActionButton)}
                </PrimaryButton>
              )}
            </Space>
          </Space>
        </MainContentStyled>

        <SpendTrackerSidebarStyled showSpendTracker={showSpendTracker}>
          <SpendTrackerPresenter expenseUuid={uuid} />
        </SpendTrackerSidebarStyled>
      </ExpenseDetailViewStyled>

      <ConfirmationModal
        open={isDeleteConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.deleteConfirmHeader)}
        message={intl.formatMessage(messages.deleteConfirmMessage)}
        onConfirm={async (done) => {
          await deleteExpenseReport()
          done()
        }}
        handleDismiss={closeDeleteConfirm}
      />

      <ConfirmationModal
        open={isDuplicateConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.duplicateOverrideConfirmHeader)}
        message={intl.formatMessage(messages.duplicateOverrideConfirmMessage)}
        onConfirm={async (done) => {
          await confirmDuplicate()
          done()
        }}
        handleDismiss={closeDuplicateConfirm}
      />

      <ConfirmationModal
        open={isOverBudgetConfirmOpen}
        type='warning'
        header={intl.formatMessage(messages.overBudgetConfirmHeader)}
        message={intl.formatMessage(messages.overBudgetConfirmMessage)}
        onConfirm={async (done) => {
          await confirmOverBudget()
          done()
        }}
        handleDismiss={cancelOverBudget}
      />
    </>
  )
}

ExpenseView.displayName = 'ExpenseView'
